"use client";

import { useEffect, useRef, useState, useCallback } from "react";
import Image from "next/image";
import Link from "next/link";
import { fetchPressAndMedia } from "@/helpers/getMedia";
import Pagination from "@/components/Pagination";

export default function InTheMediaSection({ initialPage = 1, initialData = null }) {
  
  const [items, setItems] = useState(() => {
    if (initialData?.data) {
      return initialData.data.map((item) => ({
        id: item.id,
        documentId: item.documentId,
        title: item.title,
        slug: item.slug,
        date: item.publish_date,
        source: item.media_channel_name,
        imageUrl: item?.Media_images?.[0]?.url ?? "/default.jpg",
      })).sort((a, b) => new Date(b.date) - new Date(a.date));
    }
    return [];
  });
  
  const [page, setPage] = useState(initialPage);
  const [isLoading, setIsLoading] = useState(false); // Start false because we have data
  const [totalPages, setTotalPages] = useState(initialData?.meta?.pagination?.pageCount || 1);
  const [hasMore, setHasMore] = useState(initialPage < (initialData?.meta?.pagination?.pageCount || 1));
  const [isManualPagination, setIsManualPagination] = useState(false);

  const loadMoreRef = useRef(null);
  const observerRef = useRef(null);
  const loadingRefLock = useRef(false);
  const isInitialMount = useRef(true);

  const PAGE_SIZE = 6;

   

  /* ---------------- FETCH FUNCTION ---------------- */
  const loadData = async (pageNumber, replace = false) => {
    if (loadingRefLock.current) return;

    // ❗ block only infinite scroll, not manual click
    if (!hasMore && !replace) return;

    loadingRefLock.current = true;
    setIsLoading(true);

    try {
      const res = await fetchPressAndMedia(pageNumber, PAGE_SIZE);

      const newItems = (res?.data || []).map((item) => ({
        id: item.id,
        documentId: item.documentId,
        title: item.title,
        slug: item.slug,
        date: item.publish_date,
        source: item.media_channel_name,
        imageUrl: item?.Media_images?.[0]?.url ?? "/default.jpg",
      }));

      setItems((prev) =>
        replace
          ? newItems // ✅ pagination click
          : [...prev, ...newItems].sort(
              (a, b) => new Date(b.date) - new Date(a.date)
            )
      );

      const total = res?.meta?.pagination?.pageCount || 1;
      setTotalPages(total);

      setHasMore(pageNumber < total);
      setPage(pageNumber);
    } catch (err) {
      console.error("Fetch error:", err);
      setHasMore(false);
    } finally {
      setIsLoading(false);
      loadingRefLock.current = false;
    }
  };

  /* ---------------- INITIAL LOAD (SEO FIX) ---------------- */
  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return; // Skip first mount because SSR provided the data
    }
    loadData(initialPage, true); // ✅ load only that page when initialPage prop changes
  }, [initialPage]);

  /* ---------------- INFINITE SCROLL ---------------- */
  const handleObserver = useCallback(
    (entries) => {
      const [entry] = entries;

      // ❗ disable scroll after manual pagination click
      if (isManualPagination) return;

      if (entry.isIntersecting && hasMore && !isLoading) {
        loadData(page + 1, false); // ✅ append mode
      }
    },
    [page, hasMore, isLoading, isManualPagination]
  );

  useEffect(() => {
    if (observerRef.current) observerRef.current.disconnect();

    observerRef.current = new IntersectionObserver(handleObserver, {
      root: null,
      rootMargin: "300px",
      threshold: 0.1,
    });

    if (loadMoreRef.current) {
      observerRef.current.observe(loadMoreRef.current);
    }

    return () => observerRef.current?.disconnect();
  }, [handleObserver]);

  /* ---------------- BUILD SEO URL ---------------- */
  const buildHref = (p) => {
    return p > 1 ? `/press-and-media?page=${p}` : `/press-and-media`;
  };

  /* ---------------- PAGINATION CLICK ---------------- */
  const handlePaginationClick = (p) => {
    setIsManualPagination(true); // stop auto scroll
    setHasMore(true);

    loadData(p, true); // replace data

    // scroll to top (UX)
    window.scrollTo({ top: 0, behavior: "smooth" });

    // update URL (SEO)
    const url =
      p > 1 ? `?page=${p}` : window.location.pathname;
    window.history.pushState({}, "", url);
  };
// Link for previous and next page
    useEffect(() => {
      // Remove previously added pagination links
      const existingPrev = document.querySelector('link[rel="prev"]');
      const existingNext = document.querySelector('link[rel="next"]');
  
      existingPrev?.remove();
      existingNext?.remove();
  
      // Add previous page link
      if (page > 1) {
        const prevLink = document.createElement("link");
        prevLink.rel = "prev";
        prevLink.href = buildHref(page - 1);
  
        document.head.appendChild(prevLink);
      }
  
      // Add next page link
      if (page < totalPages) {
        const nextLink = document.createElement("link");
        nextLink.rel = "next";
        nextLink.href = buildHref(page + 1);
  
        document.head.appendChild(nextLink);
      }
  
      // Cleanup when page changes or component unmounts
      return () => {
        document.querySelector('link[rel="prev"]')?.remove();
        document.querySelector('link[rel="next"]')?.remove();
      };
    }, [page, totalPages, buildHref]);
  /* ---------------- RENDER ---------------- */
  return (
    <section className="py-12 bg-slate-50">
      <div className="max-w-[var(--max-page-width)] px-[var(--container-padding)] mx-auto">
        {/* GRID */}
        <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {items.map((item) => (
            <MediaCard key={item.id} item={item} />
          ))}

          {isLoading &&
            [...Array(6)].map((_, i) => <SkeletonCard key={i} />)}
        </div>

        {/* SCROLL TRIGGER */}
        <div ref={loadMoreRef} className="h-10 mt-6 flex justify-center">
          {hasMore && !isManualPagination && (
            <p className="text-sm text-gray-500">Loading more...</p>
          )}
        </div>

        {/* PAGINATION (SEO + USER) */}
        <div className="mt-10">
          <Pagination
            currentPage={page}
            totalPages={totalPages}
            setPage={handlePaginationClick}
            buildHref={buildHref}
          />
        </div>
      </div>
    </section>
  );
}

/* ---------------- CARD ---------------- */
function MediaCard({ item }) {
  const formattedDate = (() => {
    const d = new Date(item.date);
    if (isNaN(d.getTime())) return item.date ?? "";
    return d.toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  })();

  return (
    <Link
      href={`/press-and-media/${item.slug}`}
      className="group flex h-full flex-col overflow-hidden border border-slate-200 bg-white shadow-sm transition-transform hover:-translate-y-1 hover:shadow-lg"
    >
      <div className="relative aspect-[4/3] bg-slate-100">
        <Image
          src={item.imageUrl}
          alt={item.title || "media"}
          fill
          className="object-contain transition duration-300 group-hover:scale-[1.03]"
        />
      </div>

      <div className="flex flex-1 flex-col gap-2 px-5 pb-4 pt-3">
        <div className="flex gap-3 text-xs text-slate-500">
          <span className="truncate max-w-[12rem]">{item.source}</span>
          <span>{formattedDate}</span>
        </div>

        <h3 className="line-clamp-3 text-sm md:text-base font-semibold text-slate-900 group-hover:text-sky-700">
          {item.title}
        </h3>
      </div>
    </Link>
  );
}

/* ---------------- SKELETON ---------------- */
function SkeletonCard() {
  return (
    <div className="flex flex-col border bg-white shadow-sm animate-pulse">
      <div className="aspect-[4/3] bg-slate-200" />
      <div className="p-4 space-y-2">
        <div className="h-3 w-24 bg-slate-200 rounded" />
        <div className="h-4 bg-slate-200 rounded" />
        <div className="h-4 bg-slate-200 rounded w-5/6" />
      </div>
    </div>
  );
}